import { DateTime } from 'luxon';
import { SubscriptionCadence, SubscriptionRun } from '@/types/DashboardSubscription';

export interface ScheduleOption<T> {
  value: T;
  label: string;
}

export interface Schedule {
  cadence: SubscriptionCadence;
  sendAtLocalTime: string;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  timeZone: string;
}

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// The scheduler moves a day the month doesn't have to its last day, so 31 means "last day".
export const LAST_DAY_OF_MONTH = 31;

const COMMON_TIME_ZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC'];

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
}

export function ordinal(day: number): string {
  const lastTwo = day % 100;
  if (lastTwo >= 11 && lastTwo <= 13) {
    return `${day}th`;
  }
  return `${day}${{ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] ?? 'th'}`;
}

export function dayOfWeekOptions(): ScheduleOption<number>[] {
  return WEEKDAYS.map((label, index) => ({ value: index + 1, label }));
}

// A subscription saved outside these choices keeps its day on edit rather than silently changing.
export function dayOfMonthOptions(current?: number | null): ScheduleOption<number>[] {
  const options = [
    { value: 1, label: '1st' },
    { value: 15, label: '15th' },
    { value: LAST_DAY_OF_MONTH, label: 'Last day' },
  ];
  if (current && !options.some((option) => option.value === current)) {
    options.push({ value: current, label: ordinal(current) });
    options.sort((a, b) => a.value - b.value);
  }
  return options;
}

// Whole hours only: the scheduler ticks on the hour, so 7:30 would arrive after 8:00.
export function hourOptions(): ScheduleOption<string>[] {
  return Array.from({ length: 24 }, (_, hour) => {
    const value = `${String(hour).padStart(2, '0')}:00`;
    return { value, label: formatSendTime(value) };
  });
}

export function formatSendTime(sendAtLocalTime: string): string {
  return DateTime.fromFormat(sendAtLocalTime.slice(0, 5), 'HH:mm').toFormat('h:mm a');
}

export function timeZoneCity(timeZone: string): string {
  return (timeZone.split('/').pop() ?? timeZone).replace(/_/g, ' ');
}

export function timeZoneOptions(current?: string, viewerZone = browserTimeZone()): ScheduleOption<string>[] {
  const zones = [...new Set([...COMMON_TIME_ZONES, viewerZone, ...(current ? [current] : [])])];
  return zones.map((zone) => ({
    value: zone,
    label: `${zone.replace(/_/g, ' ')}${zone === viewerZone ? ' (your timezone)' : ''}`,
  }));
}

function dayOfMonthLabel(dayOfMonth: number | null): string {
  return dayOfMonth === LAST_DAY_OF_MONTH ? 'last day' : ordinal(dayOfMonth ?? 1);
}

export function describeSchedule(schedule: Schedule): string {
  const time = formatSendTime(schedule.sendAtLocalTime);
  switch (schedule.cadence) {
    case 'WEEKLY':
      return `Weekly, ${WEEKDAYS[(schedule.dayOfWeek ?? 1) - 1]} at ${time}`;
    case 'MONTHLY':
      return `Monthly, ${dayOfMonthLabel(schedule.dayOfMonth)} at ${time}`;
    default:
      return `Daily at ${time}`;
  }
}

function sendsOn(schedule: Schedule, day: DateTime): boolean {
  switch (schedule.cadence) {
    case 'WEEKLY':
      return day.weekday === schedule.dayOfWeek;
    case 'MONTHLY':
      return day.day === Math.min(schedule.dayOfMonth ?? 1, day.daysInMonth ?? 28);
    default:
      return true;
  }
}

// The next send after `now`, worked out in the subscription's own timezone so daylight
// saving lands on the right date. Luxon moves a time skipped by spring-forward past the
// jump, as the scheduler does.
export function nextSend(schedule: Schedule, now: DateTime = DateTime.now()): DateTime | null {
  const [hour, minute] = schedule.sendAtLocalTime.split(':').map(Number);
  const start = now.setZone(schedule.timeZone);
  if (!start.isValid || Number.isNaN(hour)) {
    return null;
  }

  for (let offset = 0; offset <= 62; offset++) {
    const day = start.startOf('day').plus({ days: offset });
    if (!sendsOn(schedule, day)) {
      continue;
    }
    const candidate = day.set({ hour, minute: minute || 0 });
    if (candidate > now) {
      return candidate;
    }
  }
  return null;
}

function cadencePhrase(schedule: Schedule): string {
  switch (schedule.cadence) {
    case 'WEEKLY':
      return `every ${WEEKDAYS[(schedule.dayOfWeek ?? 1) - 1]}`;
    case 'MONTHLY':
      return `on the ${dayOfMonthLabel(schedule.dayOfMonth)} of each month`;
    default:
      return 'daily';
  }
}

export function arrivalPreview(schedule: Schedule, now: DateTime = DateTime.now(), viewerZone: string = browserTimeZone()): string {
  const sendsAt = `Sends ${cadencePhrase(schedule)} at ${formatSendTime(schedule.sendAtLocalTime)} ${timeZoneCity(schedule.timeZone)} time.`;
  const next = nextSend(schedule, now);
  if (!next) {
    return sendsAt;
  }

  const local = next.setZone(viewerZone);
  if (local.offset === next.offset) {
    return `${sendsAt} That's your local time.`;
  }
  const sameDay = local.toISODate() === next.toISODate();
  const localTime = sameDay ? local.toFormat('h:mm a') : local.toFormat("h:mm a 'on' cccc");
  return `${sendsAt} That's ${localTime} your time.`;
}

// When the email went out, or for a run that sent nothing, the time it was due.
export function formatRunTime(run: SubscriptionRun): string {
  return DateTime.fromISO(run.emailedAt ?? run.scheduledFor).toLocaleString(DateTime.DATETIME_MED);
}
